import { apiClient as api } from './apiClient';
import { API_BASE_URL } from '../config';

const API_BASE = '/learning-sessions';

/**
 * Learning Mode Types
 */
export type LearningModeType =
    | 'full_text'
    | 'vocabulary'
    | 'sentence_meaning'
    | 'comprehension'
    | 'notes'
    | 'dictation'
    | 'shadowing'
    | 'quiz';

/**
 * Device Platform Types
 */
export type DevicePlatform = 'web' | 'ios' | 'android';

/**
 * Session Status
 */
export type SessionStatus = 'active' | 'completed' | 'abandoned';

/**
 * End Session Reason
 */
export type EndSessionReason = 'completed' | 'mode_switch' | 'manual' | 'navigation' | 'page_unload';

/**
 * Learning Session
 */
export interface LearningSession {
    id: string;
    userId: string;
    courseId: string;
    mode: LearningModeType;
    status: SessionStatus;
    startedAt: string;
    endedAt: string | null;
    lastHeartbeatAt: string;
    durationSeconds: number;
    platform: DevicePlatform;
    deviceType: string | null;
    userAgent: string | null;
    contextData: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
}

/**
 * Start Session Request
 */
export interface StartSessionDto {
    courseId: string;
    mode: LearningModeType;
    platform?: DevicePlatform;
    deviceType?: string;
    contextData?: {
        sentenceIndex?: number;
        vocabularyWordId?: string;
        noteId?: string;
        quizQuestionIndex?: number;
    };
}

/**
 * Start Session Response
 */
export interface StartSessionResponse {
    sessionId: string;
    startedAt: string;
}

/**
 * Heartbeat Request
 */
export interface HeartbeatDto {
    contextData?: Record<string, unknown>;
}

/**
 * Heartbeat Response
 */
export interface HeartbeatResponse {
    sessionId: string;
    durationSeconds: number;
    lastHeartbeatAt: string;
}

/**
 * End Session Request
 */
export interface EndSessionDto {
    reason?: EndSessionReason;
}

/**
 * End Session Response
 */
export interface EndSessionResponse {
    sessionId: string;
    durationSeconds: number;
    startedAt: string;
    endedAt: string;
}

/**
 * Mode Time Statistics
 */
export interface ModeTimeResponse {
    courseId: string;
    totalSeconds: number;
    byMode: {
        [key in LearningModeType]?: {
            totalSeconds: number;
            sessionCount: number;
            lastSessionAt: string;
        };
    };
}

/**
 * Learning Session Service
 * Handles all API calls for learning session tracking
 */
export const learningSessionService = {
    /**
     * Start a new learning session
     */
    async startSession(dto: StartSessionDto): Promise<StartSessionResponse> {
        const response = await api.post(`${API_BASE}/start`, dto);
        return response.data;
    },

    /**
     * Send heartbeat to keep session alive
     */
    async heartbeat(sessionId: string, dto: HeartbeatDto = {}): Promise<HeartbeatResponse> {
        const response = await api.put(`${API_BASE}/${sessionId}/heartbeat`, dto);
        return response.data;
    },

    /**
     * End a learning session
     */
    async endSession(sessionId: string, dto: EndSessionDto = {}): Promise<EndSessionResponse> {
        const response = await api.put(`${API_BASE}/${sessionId}/end`, dto);
        return response.data;
    },

    /**
     * End session using sendBeacon (for page unload)
     * Note: This is a fire-and-forget operation
     */
    endSessionBeacon(sessionId: string, reason: EndSessionReason = 'page_unload'): boolean {
        const token = localStorage.getItem('accessToken');
        if (!token) return false;

        const url = `${API_BASE_URL}${API_BASE}/${sessionId}/end`;
        const data = JSON.stringify({ reason });

        // Use sendBeacon for reliable delivery during page unload
        // Note: sendBeacon doesn't support custom headers, but we'll handle this via a special endpoint or cookie
        return navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }));
    },

    /**
     * Get active session for current user
     */
    async getActiveSession(): Promise<LearningSession | null> {
        const response = await api.get(`${API_BASE}/active`);
        return response.data.session;
    },

    /**
     * Get time spent per mode for a course
     */
    async getModeTime(courseId: string): Promise<ModeTimeResponse> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/time`);
        return response.data;
    },
};
